import { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import keywordsApi, {
  useGetCompetitorDomainMetricsQuery,
  useGetCompetitorsAvgRankChartQuery,
  useGetCompetitorsChartQuery,
  useGetCompetitorsFuncMutation,
  useGetCompetitorsQuery,
  useGetTariffInfoQuery
} from '@/app/KeywordTracker/api/keywordsApi';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import RanksDistCard from '../components/competitors/RanksDistCard';
import GrowthAndDrops from '../components/competitors/GrowthAndDrops';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { competitorsColumnConfig } from '@/app/KeywordTracker/config/competitorsColumnConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '@/components/common/Flex';
import TimePicker from '../components/common/TimePicker';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import TrackerKeywordsInactiveAlert from '../components/TrackerKeywordsInactiveAlert';
import IconAlert from '@/components/common/IconAlert';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import CardBarChartLoading from '../../common/components/skeleton/CardBarChartLoading';
import { useExportTableMutation } from '@/app/KeywordTracker/api/exportApi';
import GrowthAndDropsSkeleton from '../components/competitors/GrowthAndDropsSkeleton';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import ManageCompetitorDrawer from '../components/competitors/ManageCompetitorDrawer';
import CompetitorsAvgRank from '../components/competitors/CompetitorsAvgRank';
import CompetitorDomainMetricsCharts from '../components/competitors/CompetitorDomainMetricsCharts';
import CompetitorRankDrawer from '../components/keywords/drawers/CompetitorRankDrawer';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import {
  isTrackerKeywordsTariffZero,
  TRACKER_KEYWORDS_INACTIVE_TOOLTIP
} from '@/app/KeywordTracker/utils/trackerTariff';
import TrackerVideoPreload from '@/app/KeywordTracker/components/common/TrackerVideoPreload';
import type { OutletContext } from './pageTypes';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

const Competitors = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useGetUserInfoQuery();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const { domain, domain_slug } = useDomain();
  const dispatch = useDispatch();

  useEffect(() => {
    setBreadcrumbData([{ text: 'رقیب', href: '#' }]);
  }, [setBreadcrumbData]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    dispatch(keywordsApi.util.invalidateTags(['tags']));
  }, [dispatch, domain, domain_slug]);

  const {
    isFetching,
    data,
    isLoading,
    isError,
    error,
    refetch
  } = useGetCompetitorsQuery({
    domain_slug,
    from_date: Number(searchParams.get('from_date')),
    to_date: Number(searchParams.get('to_date')),
    page: 1,
    perPage: 50
  });
  const [exportTable] = useExportTableMutation();
  const [getCompetitors] = useGetCompetitorsFuncMutation();
  const [showAddCompetitor, setShowAddCompetitor] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const {
    isFetching: isFetchingChart,
    data: chartData,
    isError: isChartError,
    error: chartError,
    refetch: refetchChart
  } = useGetCompetitorsChartQuery({
    from_date: Number(searchParams.get('from_date')),
    to_date: Number(searchParams.get('to_date')),
    domain_slug
  });
  const {
    isFetching: isFetchingAvgChart,
    data: chartAvgData,
    isError: isAvgChartError,
    error: avgChartError,
    refetch: refetchAvgChart
  } = useGetCompetitorsAvgRankChartQuery({
    from_date: Number(searchParams.get('from_date')),
    to_date: Number(searchParams.get('to_date')),
    domain_slug
  });
  const {
    data: domainMetricsData,
    isFetching: isFetchingDomainMetrics,
    isError: isDomainMetricsError,
    error: domainMetricsError,
    refetch: refetchDomainMetrics
  } = useGetCompetitorDomainMetricsQuery(
    { domain_slug },
    { skip: !domain_slug }
  );
  const isTrackerAddonInactive = () =>
    isTrackerKeywordsTariffZero(trackerInfo);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;
  const isCompetitorsOver = () =>
    (trackerInfo?.data?.used?.num_of_competitors ?? 0) >
    (trackerInfo?.data?.tariff?.num_of_competitors ?? 0);

  const getManageCompetitorTooltip = () => {
    if (isTrackerAddonInactive()) {
      return TRACKER_KEYWORDS_INACTIVE_TOOLTIP;
    }
    if (isCompetitorsOver()) {
      return 'تعداد رقیب ها با طرح خریداری شده مطابقت ندارد.';
    }
    if (!isAccessAllowed()) {
      return 'شما دسترسی لازم را ندارید';
    }
    return '';
  };
  return (
    <>
      <ManageCompetitorDrawer
        domain={domain}
        domain_slug={domain_slug}
        setShow={setShowAddCompetitor}
        show={showAddCompetitor}
      />
      <CompetitorRankDrawer />
      <Row className="g-3 g-md-0 my-3 p-0 justify-content-between">
        <Col xs={12} sm="auto">
          <Flex alignItems="center" className="gap-2 w-100">
            <ChildrensTooltip
              className="w-100"
              placement="top"
              html={getManageCompetitorTooltip()}
            >
              <Button
                onClick={() => setShowAddCompetitor(true)}
                variant="falcon-primary"
                size="sm"
                disabled={isTrackerAddonInactive() || !isAccessAllowed()}
                className="d-flex w-100 py-1 gap-2 justify-content-center align-items-center"
              >
                {isTrackerAddonInactive() ? (
                  <FontAwesomeIcon icon="lock" className="fs--1" />
                ) : (
                  <FontAwesomeIcon icon="plus" />
                )}
                مدیریت رقیب
              </Button>
            </ChildrensTooltip>
          </Flex>
        </Col>

        <Col xs={12} sm="auto" className="mb-3 mb-md-0">
          <Row className="g-2 p-0 gy-md-0 h-100 align-items-center">
            <Col xs="auto">
              <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
            </Col>
            <Col xs={12} md="auto">
              {!isFetching && !isLoading ? (
                <TimePicker
                  initDay={data?.data?.from_date}
                  lastDay={data?.data?.to_date}
                />
              ) : (
                <TextLoading
                  width={isMdUp ? 205 : 100}
                  height={24}
                  isWidthPx={isMdUp}
                />
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {!isFetchingTrackerInfo && isTrackerAddonInactive() ? (
        <Col xxl={12}>
          <TrackerKeywordsInactiveAlert />
          <TrackerVideoPreload src="https://static.mizfa.tools/2026/01/training-keyword-tracker-competitors.mp4" />
        </Col>
      ) : (
        <>
      <div className="mb-3">
        {!isFetching ? (
          <FiltersComponent
            preQueries={
              searchParams.get('from_date') || searchParams.get('to_date')
                ? `from_date=${searchParams.get('from_date')}&to_date=${searchParams.get('to_date')}`
                : ''
            }
            columns={competitorsColumnConfig([], isMdUp)}
            total={data?.data?.total}
          />
        ) : (
          <FiltersComponentSkeleton />
        )}
      </div>
      <div className="mb-3">
        {shouldShowDataLoadError(isAvgChartError, avgChartError) ? (
          <DataLoadError
            error={avgChartError}
            onRetry={() => refetchAvgChart()}
            isRetrying={isFetchingAvgChart}
            isNetworkError={isNetworkApiError(avgChartError)}
          />
        ) : !isFetchingAvgChart ? (
          <CompetitorsAvgRank data={chartAvgData?.data} />
        ) : (
          <CardBarChartLoading
            title="میانگین رتبه رقبا"
            limit={25}
            width={20}
            height={150}
            margin={20}
            marginBetweenLine={10}
          />
        )}
      </div>
      <CompetitorDomainMetricsCharts
        data={domainMetricsData?.data}
        isFetching={isFetchingDomainMetrics}
        isError={isDomainMetricsError}
        error={domainMetricsError}
        onRetry={() => refetchDomainMetrics()}
      />
      <Row className="g-3 mb-3">
        {shouldShowDataLoadError(isChartError, chartError) ? (
          <Col xxl={12}>
            <DataLoadError
              error={chartError}
              onRetry={() => refetchChart()}
              isRetrying={isFetchingChart}
              isNetworkError={isNetworkApiError(chartError)}
            />
          </Col>
        ) : (
          <>
            <Col lg={7} xxl={8}>
              {!isFetchingChart ? (
                <RanksDistCard
                  competitors={chartData?.data?.competitors}
                  ranks={{
                    ranks_zero_three: chartData?.data?.ranks_zero_three,
                    ranks_four_then: chartData?.data?.ranks_four_then,
                    ranks_eleven_fifty: chartData?.data?.ranks_eleven_fifty,
                    ranks_fifty_one_hundred:
                      chartData?.data?.ranks_fifty_one_hundred,
                    ranks_above_hundred: chartData?.data?.ranks_above_hundred
                  }}
                />
              ) : (
                <CardBarChartLoading
                  limit={14}
                  title={'توزیع رتبه '}
                  width={25}
                  height={228}
                  margin={15}
                  marginBetweenLine={10}
                  showFooter={false}
                />
              )}
            </Col>
            <Col lg={5} xxl={4}>
              {!isFetchingChart ? (
                <GrowthAndDrops
                  competitors={chartData?.data?.competitors}
                  growth={chartData?.data?.ranks_growth}
                  drops={chartData?.data?.ranks_drops}
                  unchanged={chartData?.data?.ranks_unchanged}
                />
              ) : (
                <GrowthAndDropsSkeleton
                  width={200}
                  limit={12}
                  margin={25}
                  title={<> افت / رشد</>}
                />
              )}
            </Col>
          </>
        )}
      </Row>

      {shouldShowDataLoadError(isError, error) ? (
        <DataLoadError
          error={error}
          onRetry={() => refetch()}
          isRetrying={isFetching}
          isNetworkError={isNetworkApiError(error)}
        />
      ) : !isFetching ? (
        <>
          <div ref={gridRef}>
            {isCompetitorsOver() && (
              <IconAlert variant="warning">
                <Flex direction="column" className="gap-1">
                  {isCompetitorsOver() && (
                    <div>
                      درحال حاضر
                      <strong className="mx-2">
                        {trackerInfo?.data?.used?.num_of_competitors}
                      </strong>
                      رقیب داری و با توجه به طرح انتخابی باید تعدادش رو به
                      <strong className="mx-2">
                        {trackerInfo?.data?.tariff?.num_of_competitors}
                      </strong>
                      برسونی (
                      <strong className="mx-2">
                        {(trackerInfo?.data?.used?.num_of_competitors ?? 0) -
                          (trackerInfo?.data?.tariff?.num_of_competitors ?? 0)}
                      </strong>
                      رقیب باید حذف بشه)
                    </div>
                  )}
                </Flex>
              </IconAlert>
            )}
            <AgGridWrapper
              otherButtons={
                <div className="d-flex gap-1 align-items-center"></div>
              }
              table_name={location.pathname.split('/').slice(1, 3).join('/')}
              settings={{
                pagination: true,
                fullscreen: true,
                changeHeight: false,
                resetGrid: false,
                saveGrid: false,
                additionalData: { domain_slug },
                export: {
                  enabled: true,
                  fetchFunction: exportTable
                }
              }}
              columns={competitorsColumnConfig(data?.data?.columns, isMdUp)}
              total={data?.data?.total}
              additionalData={{
                from_date: Number(searchParams.get('from_date')),
                to_date: Number(searchParams.get('to_date')),
                domain_slug
              }}
              data={data?.data?.rows}
              height="746px"
              fetchFunction={getCompetitors}
            />
          </div>
        </>
      ) : (
        <AgGridSkeleton
          height="696px"
          settings={{
            pagination: false,
            fullscreen: false,
            changeHeight: false,
            resetGrid: false,
            saveGrid: false,
            exportBtn: false
          }}
        />
      )}
        </>
      )}
    </>
  );
};

export default Competitors;
